import * as React from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { OPERATING_MODE_OPTIONS } from "@/types/alfred-config"
import type {
  OperatingMode,
  ProVenueInstance,
  ProVenueInstanceInput,
  DisplayConfigurationItem,
  AppleVendor,
  GoogleVendor,
} from "@/types/alfred-config"
import type { AlfredClientConfig } from "@/types/alfred-client"
import type {
  AlfredDeviceConfig,
  AlfredDeviceUI,
  CloneAlfredDeviceRequest,
  CloneField,
  CreateAlfredDeviceRequest,
  UpdateAlfredDeviceRequest,
} from "@/types/alfred-device"
import { ProVenueInstanceEditor } from "@/components/alfred/pro-venue-instance-editor"
import { DisplayConfigurationEditor } from "@/components/alfred/display-configuration-editor"
import { AppleVendorEditor } from "@/components/alfred/apple-vendor-editor"
import { GoogleVendorEditor } from "@/components/alfred/google-vendor-editor"
import { MaskedCredentialInput } from "@/components/shared/masked-credential-input"
import { CharacterCounter } from "@/components/shared/character-counter"
import { MAX_ALFRED_INDEXED_STRING_LENGTH } from "@/lib/alfred-field-validation"
import {
  ALFRED_DEVICE_SECTION_TITLES,
  getAlfredDeviceFieldLabel,
} from "@/components/alfred/alfred-device-field-presentation"

// ─── Option constants ────────────────────────────────────────────────────────

export const VALIDATION_VENDOR_OPTIONS = [
  { value: "ProVenue", label: "ProVenue" },
  { value: "Ticketmaster", label: "Ticketmaster" },
]

export const OFFLINE_BATCH_OPTIONS = [10, 25, 50, 100, 200].map((v) => ({ value: String(v), label: String(v) }))

export const SOUND_VALID_OPTIONS = [
  { value: "0", label: "Valid 1" },
  { value: "1", label: "Valid 2" },
  { value: "2", label: "Valid 3" },
  { value: "3", label: "Valid 4" },
  { value: "4", label: "OFF" },
]

export const SOUND_INVALID_OPTIONS = [
  { value: "0", label: "Invalid 1" },
  { value: "1", label: "Invalid 2" },
  { value: "2", label: "Invalid 3" },
  { value: "3", label: "Invalid 4" },
  { value: "4", label: "Invalid 5" },
  { value: "5", label: "OFF" },
]

export const SOUND_WARNING_OPTIONS = [
  { value: "0", label: "Warning 1" },
  { value: "1", label: "Warning 2" },
  { value: "2", label: "Warning 3" },
  { value: "3", label: "OFF" },
]

export const ADMIN_IDLE_OPTIONS = [
  { value: "15000", label: "15 seconds" },
  { value: "30000", label: "30 seconds" },
  { value: "60000", label: "1 minute" },
  { value: "120000", label: "2 minutes" },
  { value: "300000", label: "5 minutes" },
]

export const DISPLAY_TIME_OPTIONS = [
  { value: "1000", label: "1 second" },
  { value: "2000", label: "2 seconds" },
  { value: "3000", label: "3 seconds" },
  { value: "4000", label: "4 seconds" },
  { value: "5000", label: "5 seconds" },
]

export const UNLOCKED_ACCESS_OPTIONS = [
  { value: "2000", label: "2 seconds" },
  { value: "3000", label: "3 seconds" },
  { value: "4000", label: "4 seconds" },
  { value: "5000", label: "5 seconds" },
  { value: "6000", label: "6 seconds" },
  { value: "7000", label: "7 seconds" },
  { value: "8000", label: "8 seconds" },
  { value: "9000", label: "9 seconds" },
  { value: "10000", label: "10 seconds" },
  { value: "11000", label: "11 seconds" },
  { value: "12000", label: "12 seconds" },
  { value: "13000", label: "13 seconds" },
  { value: "14000", label: "14 seconds" },
  { value: "15000", label: "15 seconds" },
]

export const TURNSTILE_MODE_OPTIONS = [
  { value: "0", label: "NORMAL" },
  { value: "1", label: "DROP_ARM" },
  { value: "2", label: "FREE_SPIN" },
]

// ─── Form data type ───────────────────────────────────────────────────────────

export interface AlfredDeviceFormData {
  deviceId: string
  clientId: string
  sotiDeviceId: string
  validationVendor: string
  proVenueUser: string
  facilityCode: string
  league: string
  areaCode: string
  deviceCode: string
  offlineValidationMode: boolean | null
  offlineBatchUploadLimit: string
  soundValidNumber: string
  soundInvalidNumber: string
  soundWarningNumber: string
  maxCpuTemperature: string
  minCpuTemperature: string
  enableFan: boolean | null
  exitScanningAllowed: boolean | null
  useSimpleResponse: boolean | null
  adminModeIdleWindow: string
  validDisplayTime: string
  invalidDisplayTime: string
  unlockedAccessTime: string
  operatingMode: string
  turnstileMode: string
  isLockFailSafe: boolean | null
  mainBackgroundColor: string
  secondaryBackgroundColor: string
  idleStateColor: string
  nfcEnabled: boolean | null
  enableHotspot: boolean | null
  wakeLockEnabled: boolean | null
  enableScanReset: boolean | null
  enableManualValidationMask: boolean | null
  ticketmasterVenueId: string
  ticketmasterConsumerKey: string
  configurationPassword: string
  proVenueInstances: ProVenueInstanceInput[]
  displayConfiguration: DisplayConfigurationItem[]
  appleVendors: AppleVendor[]
  googleVendors: GoogleVendor[]
}

export type CloneProtectedFieldMode = "clone" | "replace" | "global"
export type CloneProtectedFieldModes = Record<CloneField, CloneProtectedFieldMode>

export const DEFAULT_CLONE_PROTECTED_FIELD_MODES: CloneProtectedFieldModes = {
  configurationPassword: "clone",
  proVenueInstances: "clone",
  ticketmasterConsumerKey: "clone",
  appleVendors: "clone",
  googleVendors: "clone",
}

export const EMPTY_FORM_DATA: AlfredDeviceFormData = {
  deviceId: "",
  clientId: "",
  sotiDeviceId: "",
  validationVendor: "",
  proVenueUser: "",
  facilityCode: "",
  league: "",
  areaCode: "",
  deviceCode: "",
  offlineValidationMode: null,
  offlineBatchUploadLimit: "",
  soundValidNumber: "",
  soundInvalidNumber: "",
  soundWarningNumber: "",
  maxCpuTemperature: "",
  minCpuTemperature: "",
  enableFan: null,
  exitScanningAllowed: null,
  useSimpleResponse: null,
  adminModeIdleWindow: "",
  validDisplayTime: "",
  invalidDisplayTime: "",
  unlockedAccessTime: "",
  operatingMode: "",
  turnstileMode: "",
  isLockFailSafe: null,
  mainBackgroundColor: "",
  secondaryBackgroundColor: "",
  idleStateColor: "",
  nfcEnabled: null,
  enableHotspot: null,
  wakeLockEnabled: null,
  enableScanReset: null,
  enableManualValidationMask: null,
  ticketmasterVenueId: "",
  ticketmasterConsumerKey: "",
  configurationPassword: "",
  proVenueInstances: [],
  displayConfiguration: [],
  appleVendors: [],
  googleVendors: [],
}

// ─── Conversion utilities ─────────────────────────────────────────────────────

function proVenueInstancesToFormData(
  instances: ProVenueInstance[],
): ProVenueInstanceInput[] {
  return instances.map((instance) => ({
    ...instance,
    consumerKey: "",
    consumerSecret: "",
  }))
}

export function deviceToFormData(device: AlfredDeviceConfig): AlfredDeviceFormData {
  return {
    deviceId: device.deviceId,
    clientId: device.clientId,
    sotiDeviceId: device.sotiDeviceId,
    validationVendor: device.validationVendor,
    proVenueUser: device.proVenueUser,
    facilityCode: device.facilityCode,
    league: device.league,
    areaCode: device.areaCode,
    deviceCode: device.deviceCode,
    offlineValidationMode: device.offlineValidationMode,
    offlineBatchUploadLimit: String(device.offlineBatchUploadLimit),
    soundValidNumber: String(device.soundValidNumber),
    soundInvalidNumber: String(device.soundInvalidNumber),
    soundWarningNumber: String(device.soundWarningNumber),
    maxCpuTemperature: String(device.maxCpuTemperature),
    minCpuTemperature: String(device.minCpuTemperature),
    enableFan: device.enableFan,
    exitScanningAllowed: device.exitScanningAllowed,
    useSimpleResponse: device.useSimpleResponse,
    adminModeIdleWindow: String(device.adminModeIdleWindow),
    validDisplayTime: String(device.validDisplayTime),
    invalidDisplayTime: String(device.invalidDisplayTime),
    unlockedAccessTime: String(device.unlockedAccessTime),
    operatingMode: String(device.operatingMode),
    turnstileMode: String(device.turnstileMode),
    isLockFailSafe: device.isLockFailSafe,
    mainBackgroundColor: device.mainBackgroundColor,
    secondaryBackgroundColor: device.secondaryBackgroundColor,
    idleStateColor: device.idleStateColor,
    nfcEnabled: device.nfcEnabled,
    enableHotspot: device.enableHotspot,
    wakeLockEnabled: device.wakeLockEnabled,
    enableScanReset: device.enableScanReset,
    enableManualValidationMask: device.enableManualValidationMask,
    ticketmasterVenueId: device.ticketmasterVenueId,
    ticketmasterConsumerKey: "",
    configurationPassword: "",
    proVenueInstances: proVenueInstancesToFormData(device.proVenueInstances),
    displayConfiguration: device.displayConfiguration,
    appleVendors: device.appleVendors,
    googleVendors: device.googleVendors,
  }
}

// For CLONE: pre-fill the create form from an existing device. Produces a
// faithful clone — fields the source device inherits from global stay as
// "Global default" rather than becoming explicit overrides. deviceId and
// sotiDeviceId are cleared so the user enters those device-specific values
// manually. Accepts either the list row (AlfredDeviceUI) or a freshly fetched
// full config (AlfredDeviceConfig); the mapping is identical for both.
export function cloneDeviceToFormData(device: AlfredDeviceUI | AlfredDeviceConfig): AlfredDeviceFormData {
  const data: AlfredDeviceFormData = {
    deviceId: "",
    clientId: device.clientId,
    // SOTI Device ID is device-specific and must be entered manually on clone.
    sotiDeviceId: "",
    validationVendor: device.validationVendor,
    proVenueUser: device.proVenueUser,
    facilityCode: device.facilityCode,
    league: device.league,
    areaCode: device.areaCode,
    deviceCode: device.deviceCode,
    offlineValidationMode: device.offlineValidationMode,
    offlineBatchUploadLimit: String(device.offlineBatchUploadLimit),
    soundValidNumber: String(device.soundValidNumber),
    soundInvalidNumber: String(device.soundInvalidNumber),
    soundWarningNumber: String(device.soundWarningNumber),
    maxCpuTemperature: String(device.maxCpuTemperature),
    minCpuTemperature: String(device.minCpuTemperature),
    enableFan: device.enableFan,
    exitScanningAllowed: device.exitScanningAllowed,
    useSimpleResponse: device.useSimpleResponse,
    adminModeIdleWindow: String(device.adminModeIdleWindow),
    validDisplayTime: String(device.validDisplayTime),
    invalidDisplayTime: String(device.invalidDisplayTime),
    unlockedAccessTime: String(device.unlockedAccessTime),
    operatingMode: String(device.operatingMode),
    turnstileMode: String(device.turnstileMode),
    isLockFailSafe: device.isLockFailSafe,
    mainBackgroundColor: device.mainBackgroundColor,
    secondaryBackgroundColor: device.secondaryBackgroundColor,
    idleStateColor: device.idleStateColor,
    nfcEnabled: device.nfcEnabled,
    enableHotspot: device.enableHotspot,
    wakeLockEnabled: device.wakeLockEnabled,
    enableScanReset: device.enableScanReset,
    enableManualValidationMask: device.enableManualValidationMask,
    ticketmasterVenueId: device.ticketmasterVenueId,
    // Protected values remain empty in UI state. Clone request modes determine
    // whether the backend copies them, accepts replacements, or uses defaults.
    ticketmasterConsumerKey: "",
    configurationPassword: "",
    // Readable ProVenue metadata supports preview and replacement editing; its
    // protected credentials never enter frontend state.
    proVenueInstances: proVenueInstancesToFormData(device.proVenueInstances),
    displayConfiguration: device.displayConfiguration,
    // Apple and Google cannot be copied by the clone endpoint. Preserve their
    // metadata for full replacement, but require every password to be re-entered.
    appleVendors: device.appleVendors.map((vendor) => ({ ...vendor, password: "" })),
    googleVendors: device.googleVendors.map((vendor) => ({ ...vendor, password: "" })),
  }
  for (const field of device.inheritedFromGlobal) {
    if (field in EMPTY_FORM_DATA) {
      const key = field as keyof AlfredDeviceFormData
      ;(data as unknown as Record<string, unknown>)[key] = EMPTY_FORM_DATA[key]
    }
  }
  return data
}

// For CREATE: omit empty optional string/number fields so they inherit from global
export function formDataToCreate(data: AlfredDeviceFormData, clients: AlfredClientConfig[]): CreateAlfredDeviceRequest {
  const clientNumericId = clients.find((c) => c.clientId === data.clientId)?.id ?? 0
  const req: CreateAlfredDeviceRequest = {
    deviceId: data.deviceId,
    clientId: clientNumericId,
    sotiDeviceId: data.sotiDeviceId,
  }

  if (data.validationVendor) req.validationVendor = data.validationVendor
  if (data.proVenueUser) req.proVenueUser = data.proVenueUser
  if (data.facilityCode) req.facilityCode = data.facilityCode
  if (data.league) req.league = data.league
  if (data.areaCode) req.areaCode = data.areaCode
  if (data.deviceCode) req.deviceCode = data.deviceCode
  if (data.offlineValidationMode !== null) req.offlineValidationMode = data.offlineValidationMode
  if (data.offlineBatchUploadLimit) req.offlineBatchUploadLimit = Number(data.offlineBatchUploadLimit)
  if (data.soundValidNumber) req.soundValidNumber = Number(data.soundValidNumber)
  if (data.soundInvalidNumber) req.soundInvalidNumber = Number(data.soundInvalidNumber)
  if (data.soundWarningNumber) req.soundWarningNumber = Number(data.soundWarningNumber)
  if (data.maxCpuTemperature) req.maxCpuTemperature = Number(data.maxCpuTemperature)
  if (data.minCpuTemperature) req.minCpuTemperature = Number(data.minCpuTemperature)
  if (data.enableFan !== null) req.enableFan = data.enableFan
  if (data.exitScanningAllowed !== null) req.exitScanningAllowed = data.exitScanningAllowed
  if (data.useSimpleResponse !== null) req.useSimpleResponse = data.useSimpleResponse
  if (data.adminModeIdleWindow) req.adminModeIdleWindow = Number(data.adminModeIdleWindow)
  if (data.validDisplayTime) req.validDisplayTime = Number(data.validDisplayTime)
  if (data.invalidDisplayTime) req.invalidDisplayTime = Number(data.invalidDisplayTime)
  if (data.unlockedAccessTime) req.unlockedAccessTime = Number(data.unlockedAccessTime)
  if (data.operatingMode !== "") req.operatingMode = Number(data.operatingMode) as OperatingMode
  if (data.turnstileMode !== "") req.turnstileMode = Number(data.turnstileMode)
  if (data.isLockFailSafe !== null) req.isLockFailSafe = data.isLockFailSafe
  if (data.mainBackgroundColor) req.mainBackgroundColor = data.mainBackgroundColor
  if (data.secondaryBackgroundColor) req.secondaryBackgroundColor = data.secondaryBackgroundColor
  if (data.idleStateColor) req.idleStateColor = data.idleStateColor
  if (data.nfcEnabled !== null) req.nfcEnabled = data.nfcEnabled
  if (data.enableHotspot !== null) req.enableHotspot = data.enableHotspot
  if (data.wakeLockEnabled !== null) req.wakeLockEnabled = data.wakeLockEnabled
  if (data.enableScanReset !== null) req.enableScanReset = data.enableScanReset
  if (data.enableManualValidationMask !== null) req.enableManualValidationMask = data.enableManualValidationMask
  if (data.ticketmasterVenueId) req.ticketmasterVenueId = data.ticketmasterVenueId
  if (data.ticketmasterConsumerKey) req.ticketmasterConsumerKey = data.ticketmasterConsumerKey
  if (data.proVenueInstances.length > 0) req.proVenueInstances = data.proVenueInstances
  if (data.displayConfiguration.length > 0) req.displayConfiguration = data.displayConfiguration
  if (data.appleVendors.length > 0) req.appleVendors = data.appleVendors
  if (data.googleVendors.length > 0) req.googleVendors = data.googleVendors
  req.configurationPassword = data.configurationPassword.trim() || null
  return req
}

export function formDataToClone(
  data: AlfredDeviceFormData,
  clients: AlfredClientConfig[],
  protectedFieldModes: CloneProtectedFieldModes,
): CloneAlfredDeviceRequest {
  const request: CloneAlfredDeviceRequest = {
    ...formDataToCreate(data, clients),
    cloneSensitiveFields: [],
  }

  delete request.configurationPassword
  delete request.proVenueInstances
  delete request.ticketmasterConsumerKey
  delete request.appleVendors
  delete request.googleVendors

  for (const field of Object.keys(protectedFieldModes) as CloneField[]) {
    const fieldMode = protectedFieldModes[field]
    if (fieldMode === "clone") {
      request.cloneSensitiveFields.push(field)
      continue
    }
    if (fieldMode === "global") continue

    switch (field) {
      case "configurationPassword":
        request.configurationPassword = data.configurationPassword.trim()
        break
      case "proVenueInstances":
        request.proVenueInstances = data.proVenueInstances
        break
      case "ticketmasterConsumerKey":
        request.ticketmasterConsumerKey = data.ticketmasterConsumerKey.trim()
        break
      case "appleVendors":
        request.appleVendors = data.appleVendors
        break
      case "googleVendors":
        request.googleVendors = data.googleVendors
        break
    }
  }

  return request
}

// For UPDATE (PUT): include all fields
export function formDataToUpdate(data: AlfredDeviceFormData): UpdateAlfredDeviceRequest {
  const request: UpdateAlfredDeviceRequest = {
    deviceId: data.deviceId,
    clientId: data.clientId,
    sotiDeviceId: data.sotiDeviceId,
    validationVendor: data.validationVendor,
    proVenueUser: data.proVenueUser,
    facilityCode: data.facilityCode,
    league: data.league,
    areaCode: data.areaCode,
    deviceCode: data.deviceCode,
    offlineValidationMode: data.offlineValidationMode,
    offlineBatchUploadLimit: data.offlineBatchUploadLimit ? Number(data.offlineBatchUploadLimit) : undefined,
    soundValidNumber: data.soundValidNumber ? Number(data.soundValidNumber) : undefined,
    soundInvalidNumber: data.soundInvalidNumber ? Number(data.soundInvalidNumber) : undefined,
    soundWarningNumber: data.soundWarningNumber ? Number(data.soundWarningNumber) : undefined,
    maxCpuTemperature: data.maxCpuTemperature ? Number(data.maxCpuTemperature) : undefined,
    minCpuTemperature: data.minCpuTemperature ? Number(data.minCpuTemperature) : undefined,
    enableFan: data.enableFan,
    exitScanningAllowed: data.exitScanningAllowed,
    useSimpleResponse: data.useSimpleResponse,
    adminModeIdleWindow: data.adminModeIdleWindow ? Number(data.adminModeIdleWindow) : undefined,
    validDisplayTime: data.validDisplayTime ? Number(data.validDisplayTime) : undefined,
    invalidDisplayTime: data.invalidDisplayTime ? Number(data.invalidDisplayTime) : undefined,
    unlockedAccessTime: data.unlockedAccessTime ? Number(data.unlockedAccessTime) : undefined,
    operatingMode: data.operatingMode !== "" ? (Number(data.operatingMode) as OperatingMode) : undefined,
    turnstileMode: data.turnstileMode !== "" ? Number(data.turnstileMode) : undefined,
    isLockFailSafe: data.isLockFailSafe,
    mainBackgroundColor: data.mainBackgroundColor || undefined,
    secondaryBackgroundColor: data.secondaryBackgroundColor || undefined,
    idleStateColor: data.idleStateColor || undefined,
    nfcEnabled: data.nfcEnabled,
    enableHotspot: data.enableHotspot,
    wakeLockEnabled: data.wakeLockEnabled,
    enableScanReset: data.enableScanReset,
    enableManualValidationMask: data.enableManualValidationMask,
    ticketmasterVenueId: data.ticketmasterVenueId || undefined,
  }
  const ticketmasterConsumerKey = data.ticketmasterConsumerKey.trim()
  if (ticketmasterConsumerKey) {
    request.ticketmasterConsumerKey = {
      value: ticketmasterConsumerKey,
      useDefault: false,
    }
  }
  return request
}

// ─── Local helper components ──────────────────────────────────────────────────

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-4 pt-2">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground whitespace-nowrap">
        {title}
      </h3>
      <div className="flex-1 h-px bg-border" />
    </div>
  )
}

function FieldRow({ label, required, isDefault, children }: { label: string; required?: boolean; isDefault?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-[13px] text-muted-foreground font-medium flex items-center gap-1.5">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
        {isDefault && <span className="text-[10px] font-medium text-muted-foreground/70">(default)</span>}
      </Label>
      {children}
    </div>
  )
}

const CLONE_PROTECTED_FIELD_OPTIONS: Array<{
  value: CloneProtectedFieldMode
  label: string
}> = [
  { value: "clone", label: "Copy from source" },
  { value: "replace", label: "Enter replacement" },
  { value: "global", label: "Use global default" },
]

function CloneProtectedFieldChoice({
  field,
  value,
  onChange,
  disabled,
}: {
  field: CloneField
  value: CloneProtectedFieldMode
  onChange: (value: CloneProtectedFieldMode) => void
  disabled?: boolean
}) {
  const label = getAlfredDeviceFieldLabel(field)

  return (
    <RadioGroup
      value={value}
      onValueChange={(nextValue) => onChange(nextValue as CloneProtectedFieldMode)}
      disabled={disabled}
      aria-label={`${label} value source`}
      className="grid gap-2 sm:grid-cols-3"
    >
      {CLONE_PROTECTED_FIELD_OPTIONS.map((option) => {
        const id = `${field}-${option.value}`
        return (
          <div key={option.value} className="flex items-center gap-2">
            <RadioGroupItem id={id} value={option.value} />
            <Label htmlFor={id} className="text-xs font-normal text-muted-foreground">
              {option.label}
            </Label>
          </div>
        )
      })}
    </RadioGroup>
  )
}

function SwitchRow({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  isDefault,
}: {
  label: string
  description?: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
  disabled?: boolean
  isDefault?: boolean
}) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <div className="flex-1 pr-4">
        <p className="text-[13px] text-muted-foreground font-medium flex items-center gap-1.5">
          {label}
          {isDefault && <span className="text-[10px] font-medium text-muted-foreground/70">(default)</span>}
        </p>
        {description && <p className="text-xs text-muted-foreground/60 mt-0.5">{description}</p>}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="data-[state=checked]:bg-primary"
      />
    </div>
  )
}

function ColorRow({
  label,
  value,
  onChange,
  disabled,
  isDefault,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  isDefault?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label className="text-[13px] text-muted-foreground font-medium flex items-center gap-1.5">
        {label}
        {isDefault && <span className="text-[10px] font-medium text-muted-foreground/70">(default)</span>}
      </Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 w-14 rounded border border-input cursor-pointer bg-transparent p-0.5 disabled:cursor-not-allowed disabled:opacity-60"
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          disabled={disabled}
          className="font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
        />
      </div>
    </div>
  )
}

function ArrayFieldRow({
  label,
  count,
  noun,
  warning,
  children,
}: {
  label: string
  count: number
  noun: string
  warning?: string
  children: React.ReactNode
}) {
  return (
    <FieldRow label={label}>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
          {count === 0 ? "Global default" : `${count} ${count === 1 ? noun : `${noun}s`}`}
        </span>
        {children}
      </div>
      {warning ? <p className="text-xs text-destructive">{warning}</p> : null}
    </FieldRow>
  )
}

function BooleanSelect({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null
  onChange: (v: boolean | null) => void
  disabled?: boolean
}) {
  const strValue = value === null ? "__global__" : String(value)
  return (
    <Select
      value={strValue}
      onValueChange={(v) => onChange(v === "__global__" ? null : v === "true")}
      disabled={disabled}
    >
      <SelectTrigger className="bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__global__">Global default</SelectItem>
        <SelectItem value="true">True</SelectItem>
        <SelectItem value="false">False</SelectItem>
      </SelectContent>
    </Select>
  )
}

const GLOBAL_DEFAULT_SENTINEL = "__global__"

function OptSelect({
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled,
  withGlobalDefault = false,
}: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
  disabled?: boolean
  withGlobalDefault?: boolean
}) {
  const selectValue = withGlobalDefault && value === "" ? GLOBAL_DEFAULT_SENTINEL : value
  const handleChange = (v: string) => {
    onChange(withGlobalDefault && v === GLOBAL_DEFAULT_SENTINEL ? "" : v)
  }
  return (
    <Select value={selectValue} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger className="bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {withGlobalDefault && (
          <SelectItem value={GLOBAL_DEFAULT_SENTINEL}>Global default</SelectItem>
        )}
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

// ─── Main fields component ────────────────────────────────────────────────────

interface AlfredDeviceFormFieldsProps {
  data: AlfredDeviceFormData
  onChange: (field: keyof AlfredDeviceFormData, value: string | boolean | null | unknown[]) => void
  errors?: Partial<Record<keyof AlfredDeviceFormData, string>>
  touched?: Partial<Record<keyof AlfredDeviceFormData, boolean>>
  onBlur?: (field: keyof AlfredDeviceFormData) => void
  mode: "add" | "edit"
  isClone?: boolean
  cloneProtectedFieldModes?: CloneProtectedFieldModes
  onCloneProtectedFieldModeChange?: (
    field: CloneField,
    value: CloneProtectedFieldMode,
  ) => void
  clients: AlfredClientConfig[]
  disabled?: boolean
  inheritedFromGlobal?: Set<string>
}

export function AlfredDeviceFormFields({
  data,
  onChange,
  errors = {},
  touched = {},
  onBlur,
  mode,
  isClone = false,
  cloneProtectedFieldModes = DEFAULT_CLONE_PROTECTED_FIELD_MODES,
  onCloneProtectedFieldModeChange,
  clients,
  disabled = false,
  inheritedFromGlobal,
}: AlfredDeviceFormFieldsProps) {
  const inh = (field: keyof AlfredDeviceFormData) => inheritedFromGlobal?.has(field) ?? false
  const txt = (field: keyof AlfredDeviceFormData) => ({
    value: data[field] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(field, e.target.value),
    onBlur: () => onBlur?.(field),
    disabled,
    required: mode === "add" && (
      field === "deviceId" || field === "sotiDeviceId"
    ),
    "aria-invalid": errors[field] && touched[field] ? true : undefined,
    className: [
      "font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]",
      errors[field] && touched[field] ? "border-destructive" : "",
    ].join(" "),
  })

  const fieldError = (field: keyof AlfredDeviceFormData) =>
    errors[field] && touched[field] ? (
      <p className="text-xs text-destructive">{errors[field]}</p>
    ) : null

  return (
    <div className="space-y-6">

      {/* ── 1. Device Identity ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.deviceIdentity} />

      {mode === "edit" ? (
        <div className="grid grid-cols-2 gap-3 rounded-md bg-muted/30 px-3 py-2.5 text-sm">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{getAlfredDeviceFieldLabel("deviceId")}</p>
            <p className="font-mono font-medium text-foreground">{data.deviceId}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">{getAlfredDeviceFieldLabel("clientId")}</p>
            <p className="font-mono font-medium text-foreground">{data.clientId}</p>
          </div>
        </div>
      ) : (
        <>
          <FieldRow label={getAlfredDeviceFieldLabel("deviceId")} required>
            <Input {...txt("deviceId")} placeholder="e.g., LAD-GATE-001" />
            {fieldError("deviceId")}
            <CharacterCounter
              value={data.deviceId}
              maxLength={MAX_ALFRED_INDEXED_STRING_LENGTH}
            />
          </FieldRow>
          <FieldRow label={getAlfredDeviceFieldLabel("clientId")} required>
            <Select
              value={data.clientId}
              onValueChange={(v) => onChange("clientId", v)}
              disabled={disabled}
            >
              <SelectTrigger
                className={`bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${errors.clientId && touched.clientId ? "border-destructive" : ""}`}
                onBlur={() => onBlur?.("clientId")}
                aria-invalid={errors.clientId && touched.clientId ? true : undefined}
              >
                <SelectValue placeholder="Select a client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.clientId} value={c.clientId}>
                    {c.clientId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldError("clientId")}
          </FieldRow>
        </>
      )}

      <FieldRow label={getAlfredDeviceFieldLabel("sotiDeviceId")} required={mode === "add"} isDefault={inh("sotiDeviceId")}>
        <Input {...txt("sotiDeviceId")} placeholder="e.g., soti-lad-001" />
        {fieldError("sotiDeviceId")}
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("league")} isDefault={inh("league")}>
        <Input {...txt("league")} placeholder="e.g., MLB" />
      </FieldRow>

      {/* ── 2. Validation Provider ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.validationProvider} />
      <FieldRow label={getAlfredDeviceFieldLabel("validationVendor")} isDefault={inh("validationVendor")}>
        <OptSelect
          value={data.validationVendor}
          onChange={(v) => onChange("validationVendor", v)}
          options={VALIDATION_VENDOR_OPTIONS}
          placeholder="Select vendor..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>

      {/* ── 3. ProVenue Settings ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.proVenueSettings} />
      <FieldRow label={getAlfredDeviceFieldLabel("proVenueUser")} isDefault={inh("proVenueUser")}>
        <Input {...txt("proVenueUser")} placeholder="e.g., alfred-service" />
      </FieldRow>
      {mode === "add" && (
        isClone ? (
          <FieldRow label={getAlfredDeviceFieldLabel("proVenueInstances")}>
            <CloneProtectedFieldChoice
              field="proVenueInstances"
              value={cloneProtectedFieldModes.proVenueInstances}
              onChange={(value) => onCloneProtectedFieldModeChange?.("proVenueInstances", value)}
              disabled={disabled}
            />
            {cloneProtectedFieldModes.proVenueInstances === "clone" ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                  {data.proVenueInstances.length} source {data.proVenueInstances.length === 1 ? "instance" : "instances"}
                </span>
                <ProVenueInstanceEditor
                  items={data.proVenueInstances}
                  onChange={() => undefined}
                  disabled
                  triggerAriaLabel="View source ProVenue instances"
                />
              </div>
            ) : null}
            {cloneProtectedFieldModes.proVenueInstances === "replace" ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                  {data.proVenueInstances.length === 0
                    ? "No replacements"
                    : `${data.proVenueInstances.length} replacement ${data.proVenueInstances.length === 1 ? "instance" : "instances"}`}
                </span>
                <ProVenueInstanceEditor
                  items={data.proVenueInstances}
                  onChange={(value) => onChange("proVenueInstances", value)}
                  requireSecret
                  triggerAriaLabel="Manage replacement ProVenue instances"
                />
              </div>
            ) : null}
            {cloneProtectedFieldModes.proVenueInstances === "global" ? (
              <p className="text-xs text-muted-foreground">
                The destination device will inherit all ProVenue instances from global defaults.
              </p>
            ) : null}
            {fieldError("proVenueInstances")}
          </FieldRow>
        ) : (
          <ArrayFieldRow
            label={getAlfredDeviceFieldLabel("proVenueInstances")}
            count={data.proVenueInstances.length}
            noun="instance"
            warning={errors.proVenueInstances && touched.proVenueInstances ? errors.proVenueInstances : undefined}
          >
            <ProVenueInstanceEditor
              items={data.proVenueInstances}
              onChange={(v) => onChange("proVenueInstances", v)}
              requireSecret
            />
          </ArrayFieldRow>
        )
      )}

      {/* ── 4. Device Location ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.deviceLocation} />
      <FieldRow label={getAlfredDeviceFieldLabel("facilityCode")} isDefault={inh("facilityCode")}>
        <Input {...txt("facilityCode")} placeholder="e.g., MLB-001" />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("areaCode")} isDefault={inh("areaCode")}>
        <Input {...txt("areaCode")} placeholder="e.g., main-gate" />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("deviceCode")} isDefault={inh("deviceCode")}>
        <Input {...txt("deviceCode")} placeholder="e.g., DEV-LAD-01" />
      </FieldRow>

      {/* ── 5. Offline Validation ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.offlineValidation} />
      {mode === "add" ? (
        <FieldRow label={getAlfredDeviceFieldLabel("offlineValidationMode")} isDefault={inh("offlineValidationMode")}>
          <BooleanSelect value={data.offlineValidationMode} onChange={(v) => onChange("offlineValidationMode", v)} disabled={disabled} />
        </FieldRow>
      ) : (
        <SwitchRow
          label={getAlfredDeviceFieldLabel("offlineValidationMode")}
          description="Validate tickets offline even when connectivity is available"
          checked={data.offlineValidationMode as boolean}
          onCheckedChange={(v) => onChange("offlineValidationMode", v)}
          disabled={disabled}
          isDefault={inh("offlineValidationMode")}
        />
      )}
      <FieldRow label={getAlfredDeviceFieldLabel("offlineBatchUploadLimit")} isDefault={inh("offlineBatchUploadLimit")}>
        <OptSelect
          value={data.offlineBatchUploadLimit}
          onChange={(v) => onChange("offlineBatchUploadLimit", v)}
          options={OFFLINE_BATCH_OPTIONS}
          placeholder="Select limit..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>

      {/* ── 6. Sounds ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.sounds} />
      <FieldRow label={getAlfredDeviceFieldLabel("soundValidNumber")} isDefault={inh("soundValidNumber")}>
        <OptSelect
          value={data.soundValidNumber}
          onChange={(v) => onChange("soundValidNumber", v)}
          options={SOUND_VALID_OPTIONS}
          placeholder="Select sound..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("soundInvalidNumber")} isDefault={inh("soundInvalidNumber")}>
        <OptSelect
          value={data.soundInvalidNumber}
          onChange={(v) => onChange("soundInvalidNumber", v)}
          options={SOUND_INVALID_OPTIONS}
          placeholder="Select sound..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("soundWarningNumber")} isDefault={inh("soundWarningNumber")}>
        <OptSelect
          value={data.soundWarningNumber}
          onChange={(v) => onChange("soundWarningNumber", v)}
          options={SOUND_WARNING_OPTIONS}
          placeholder="Select sound..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>

      {/* ── 7. Temperature & Cooling ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.temperatureAndCooling} />
      <FieldRow label={getAlfredDeviceFieldLabel("maxCpuTemperature")} isDefault={inh("maxCpuTemperature")}>
        <Input {...txt("maxCpuTemperature")} type="number" placeholder="e.g., 75" />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("minCpuTemperature")} isDefault={inh("minCpuTemperature")}>
        <Input {...txt("minCpuTemperature")} type="number" placeholder="e.g., 0" />
      </FieldRow>
      {mode === "add" ? (
        <FieldRow label={getAlfredDeviceFieldLabel("enableFan")} isDefault={inh("enableFan")}>
          <BooleanSelect value={data.enableFan} onChange={(v) => onChange("enableFan", v)} disabled={disabled} />
        </FieldRow>
      ) : (
        <SwitchRow
          label={getAlfredDeviceFieldLabel("enableFan")}
          checked={data.enableFan as boolean}
          onCheckedChange={(v) => onChange("enableFan", v)}
          disabled={disabled}
          isDefault={inh("enableFan")}
        />
      )}

      {/* ── 8. Validation Flow ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.validationFlow} />
      {mode === "add" ? (
        <>
          <FieldRow label={getAlfredDeviceFieldLabel("exitScanningAllowed")} isDefault={inh("exitScanningAllowed")}>
            <BooleanSelect value={data.exitScanningAllowed} onChange={(v) => onChange("exitScanningAllowed", v)} disabled={disabled} />
          </FieldRow>
          <FieldRow label={getAlfredDeviceFieldLabel("useSimpleResponse")} isDefault={inh("useSimpleResponse")}>
            <BooleanSelect value={data.useSimpleResponse} onChange={(v) => onChange("useSimpleResponse", v)} disabled={disabled} />
          </FieldRow>
        </>
      ) : (
        <>
          <SwitchRow
            label={getAlfredDeviceFieldLabel("exitScanningAllowed")}
            description="Allow devices to scan tickets for exit validation"
            checked={data.exitScanningAllowed as boolean}
            onCheckedChange={(v) => onChange("exitScanningAllowed", v)}
            disabled={disabled}
            isDefault={inh("exitScanningAllowed")}
          />
          <SwitchRow
            label={getAlfredDeviceFieldLabel("useSimpleResponse")}
            description="Show simplified validation outcomes on the device screen"
            checked={data.useSimpleResponse as boolean}
            onCheckedChange={(v) => onChange("useSimpleResponse", v)}
            disabled={disabled}
            isDefault={inh("useSimpleResponse")}
          />
        </>
      )}

      {/* ── 9. Timeouts ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.timeouts} />
      <FieldRow label={getAlfredDeviceFieldLabel("adminModeIdleWindow")} isDefault={inh("adminModeIdleWindow")}>
        <OptSelect
          value={data.adminModeIdleWindow}
          onChange={(v) => onChange("adminModeIdleWindow", v)}
          options={ADMIN_IDLE_OPTIONS}
          placeholder="Select duration..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("validDisplayTime")} isDefault={inh("validDisplayTime")}>
        <OptSelect
          value={data.validDisplayTime}
          onChange={(v) => onChange("validDisplayTime", v)}
          options={DISPLAY_TIME_OPTIONS}
          placeholder="Select duration..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("invalidDisplayTime")} isDefault={inh("invalidDisplayTime")}>
        <OptSelect
          value={data.invalidDisplayTime}
          onChange={(v) => onChange("invalidDisplayTime", v)}
          options={DISPLAY_TIME_OPTIONS}
          placeholder="Select duration..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("unlockedAccessTime")} isDefault={inh("unlockedAccessTime")}>
        <OptSelect
          value={data.unlockedAccessTime}
          onChange={(v) => onChange("unlockedAccessTime", v)}
          options={UNLOCKED_ACCESS_OPTIONS}
          placeholder="Select duration..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>

      {/* ── 10. Operating Modes ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.operatingModes} />
      <FieldRow label={getAlfredDeviceFieldLabel("operatingMode")} isDefault={inh("operatingMode")}>
        <OptSelect
          value={data.operatingMode}
          onChange={(v) => onChange("operatingMode", v)}
          options={OPERATING_MODE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
          placeholder="Select mode..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("turnstileMode")} isDefault={inh("turnstileMode")}>
        <OptSelect
          value={data.turnstileMode}
          onChange={(v) => onChange("turnstileMode", v)}
          options={TURNSTILE_MODE_OPTIONS}
          placeholder="Select mode..."
          disabled={disabled}
          withGlobalDefault
        />
      </FieldRow>
      {mode === "add" ? (
        <FieldRow label={getAlfredDeviceFieldLabel("isLockFailSafe")} isDefault={inh("isLockFailSafe")}>
          <BooleanSelect value={data.isLockFailSafe} onChange={(v) => onChange("isLockFailSafe", v)} disabled={disabled} />
        </FieldRow>
      ) : (
        <SwitchRow
          label={getAlfredDeviceFieldLabel("isLockFailSafe")}
          description="Unlock the turnstile if power is lost"
          checked={data.isLockFailSafe as boolean}
          onCheckedChange={(v) => onChange("isLockFailSafe", v)}
          disabled={disabled}
          isDefault={inh("isLockFailSafe")}
        />
      )}

      {/* ── 11. Appearance ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.appearance} />
      <ColorRow
        label={getAlfredDeviceFieldLabel("mainBackgroundColor")}
        value={data.mainBackgroundColor}
        onChange={(v) => onChange("mainBackgroundColor", v)}
        disabled={disabled}
        isDefault={inh("mainBackgroundColor")}
      />
      <ColorRow
        label={getAlfredDeviceFieldLabel("secondaryBackgroundColor")}
        value={data.secondaryBackgroundColor}
        onChange={(v) => onChange("secondaryBackgroundColor", v)}
        disabled={disabled}
        isDefault={inh("secondaryBackgroundColor")}
      />
      <ColorRow
        label={getAlfredDeviceFieldLabel("idleStateColor")}
        value={data.idleStateColor}
        onChange={(v) => onChange("idleStateColor", v)}
        disabled={disabled}
        isDefault={inh("idleStateColor")}
      />

      {/* ── 12. Features ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.features} />
      {mode === "add" ? (
        <>
          <FieldRow label={getAlfredDeviceFieldLabel("nfcEnabled")} isDefault={inh("nfcEnabled")}>
            <BooleanSelect value={data.nfcEnabled} onChange={(v) => onChange("nfcEnabled", v)} disabled={disabled} />
          </FieldRow>
          <FieldRow label={getAlfredDeviceFieldLabel("enableHotspot")} isDefault={inh("enableHotspot")}>
            <BooleanSelect value={data.enableHotspot} onChange={(v) => onChange("enableHotspot", v)} disabled={disabled} />
          </FieldRow>
          <FieldRow label={getAlfredDeviceFieldLabel("wakeLockEnabled")} isDefault={inh("wakeLockEnabled")}>
            <BooleanSelect value={data.wakeLockEnabled} onChange={(v) => onChange("wakeLockEnabled", v)} disabled={disabled} />
          </FieldRow>
          <FieldRow label={getAlfredDeviceFieldLabel("enableScanReset")} isDefault={inh("enableScanReset")}>
            <BooleanSelect value={data.enableScanReset} onChange={(v) => onChange("enableScanReset", v)} disabled={disabled} />
          </FieldRow>
          <FieldRow label={getAlfredDeviceFieldLabel("enableManualValidationMask")} isDefault={inh("enableManualValidationMask")}>
            <BooleanSelect value={data.enableManualValidationMask} onChange={(v) => onChange("enableManualValidationMask", v)} disabled={disabled} />
          </FieldRow>
        </>
      ) : (
        <>
          <SwitchRow
            label={getAlfredDeviceFieldLabel("nfcEnabled")}
            description="Show 'Tap or Scan' instead of 'Scan only'"
            checked={data.nfcEnabled as boolean}
            onCheckedChange={(v) => onChange("nfcEnabled", v)}
            disabled={disabled}
            isDefault={inh("nfcEnabled")}
          />
          <SwitchRow
            label={getAlfredDeviceFieldLabel("enableHotspot")}
            checked={data.enableHotspot as boolean}
            onCheckedChange={(v) => onChange("enableHotspot", v)}
            disabled={disabled}
            isDefault={inh("enableHotspot")}
          />
          <SwitchRow
            label={getAlfredDeviceFieldLabel("wakeLockEnabled")}
            checked={data.wakeLockEnabled as boolean}
            onCheckedChange={(v) => onChange("wakeLockEnabled", v)}
            disabled={disabled}
            isDefault={inh("wakeLockEnabled")}
          />
          <SwitchRow
            label={getAlfredDeviceFieldLabel("enableScanReset")}
            description="Show Scan Reset option in navigation"
            checked={data.enableScanReset as boolean}
            onCheckedChange={(v) => onChange("enableScanReset", v)}
            disabled={disabled}
            isDefault={inh("enableScanReset")}
          />
          <SwitchRow
            label={getAlfredDeviceFieldLabel("enableManualValidationMask")}
            checked={data.enableManualValidationMask as boolean}
            onCheckedChange={(v) => onChange("enableManualValidationMask", v)}
            disabled={disabled}
            isDefault={inh("enableManualValidationMask")}
          />
        </>
      )}

      {/* ── 13. Vendor Credentials ── */}
      {mode === "add" && (
        <>
          <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.vendorCredentials} />
          {isClone ? (
            <FieldRow label={getAlfredDeviceFieldLabel("appleVendors")}>
              <CloneProtectedFieldChoice
                field="appleVendors"
                value={cloneProtectedFieldModes.appleVendors}
                onChange={(value) => onCloneProtectedFieldModeChange?.("appleVendors", value)}
                disabled={disabled}
              />
              {cloneProtectedFieldModes.appleVendors === "clone" ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                      {data.appleVendors.length} source {data.appleVendors.length === 1 ? "credential" : "credentials"}
                    </span>
                    <AppleVendorEditor
                      items={data.appleVendors}
                      onChange={() => undefined}
                      disabled
                      triggerAriaLabel="View source Apple vendor credentials"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The backend will securely copy all source Apple vendor credentials without
                    displaying their passwords.
                  </p>
                </>
              ) : null}
              {cloneProtectedFieldModes.appleVendors === "replace" ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                      {data.appleVendors.length} replacement {data.appleVendors.length === 1 ? "credential" : "credentials"}
                    </span>
                    <AppleVendorEditor
                      items={data.appleVendors}
                      onChange={(value) => onChange("appleVendors", value)}
                      requireSecret
                      disabled={disabled}
                      triggerAriaLabel="Manage replacement Apple vendor credentials"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Source passwords stay hidden. Enter a new password for every credential to
                    replace the full Apple vendor collection.
                  </p>
                  {fieldError("appleVendors")}
                </>
              ) : null}
              {cloneProtectedFieldModes.appleVendors === "global" ? (
                <p className="text-xs text-muted-foreground">
                  The destination device will inherit Apple vendor credentials from global defaults
                  and continue receiving global changes.
                </p>
              ) : null}
            </FieldRow>
          ) : (
            <ArrayFieldRow
              label={getAlfredDeviceFieldLabel("appleVendors")}
              count={data.appleVendors.length}
              noun="credential"
              warning={errors.appleVendors && touched.appleVendors ? errors.appleVendors : undefined}
            >
              <AppleVendorEditor
                items={data.appleVendors}
                onChange={(v) => onChange("appleVendors", v)}
                requireSecret
              />
            </ArrayFieldRow>
          )}
          {isClone ? (
            <FieldRow label={getAlfredDeviceFieldLabel("googleVendors")}>
              <CloneProtectedFieldChoice
                field="googleVendors"
                value={cloneProtectedFieldModes.googleVendors}
                onChange={(value) => onCloneProtectedFieldModeChange?.("googleVendors", value)}
                disabled={disabled}
              />
              {cloneProtectedFieldModes.googleVendors === "clone" ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                      {data.googleVendors.length} source {data.googleVendors.length === 1 ? "credential" : "credentials"}
                    </span>
                    <GoogleVendorEditor
                      items={data.googleVendors}
                      onChange={() => undefined}
                      disabled
                      triggerAriaLabel="View source Google vendor credentials"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The backend will securely copy all source Google vendor credentials without
                    displaying their passwords.
                  </p>
                </>
              ) : null}
              {cloneProtectedFieldModes.googleVendors === "replace" ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                      {data.googleVendors.length} replacement {data.googleVendors.length === 1 ? "credential" : "credentials"}
                    </span>
                    <GoogleVendorEditor
                      items={data.googleVendors}
                      onChange={(value) => onChange("googleVendors", value)}
                      requireSecret
                      disabled={disabled}
                      triggerAriaLabel="Manage replacement Google vendor credentials"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Source passwords stay hidden. Enter a new password for every credential to
                    replace the full Google vendor collection.
                  </p>
                  {fieldError("googleVendors")}
                </>
              ) : null}
              {cloneProtectedFieldModes.googleVendors === "global" ? (
                <p className="text-xs text-muted-foreground">
                  The destination device will inherit Google vendor credentials from global defaults
                  and continue receiving global changes.
                </p>
              ) : null}
            </FieldRow>
          ) : (
            <ArrayFieldRow
              label={getAlfredDeviceFieldLabel("googleVendors")}
              count={data.googleVendors.length}
              noun="credential"
              warning={errors.googleVendors && touched.googleVendors ? errors.googleVendors : undefined}
            >
              <GoogleVendorEditor
                items={data.googleVendors}
                onChange={(v) => onChange("googleVendors", v)}
                requireSecret
              />
            </ArrayFieldRow>
          )}
          {/* ── 14. Display Configuration ── */}
          <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.displayConfiguration} />
          <ArrayFieldRow label={getAlfredDeviceFieldLabel("displayConfiguration")} count={data.displayConfiguration.length} noun="screen">
            <DisplayConfigurationEditor
              items={data.displayConfiguration}
              onChange={(v) => onChange("displayConfiguration", v)}
            />
          </ArrayFieldRow>
        </>
      )}

      {/* ── 15. Ticketmaster Settings ── */}
      <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.ticketmasterSettings} />
      <FieldRow label={getAlfredDeviceFieldLabel("ticketmasterVenueId")} isDefault={inh("ticketmasterVenueId")}>
        <Input {...txt("ticketmasterVenueId")} placeholder="e.g., TM-LAD-VENUE" />
      </FieldRow>
      <FieldRow label={getAlfredDeviceFieldLabel("ticketmasterConsumerKey")} isDefault={inh("ticketmasterConsumerKey")}>
        {isClone ? (
          <>
            <CloneProtectedFieldChoice
              field="ticketmasterConsumerKey"
              value={cloneProtectedFieldModes.ticketmasterConsumerKey}
              onChange={(value) => onCloneProtectedFieldModeChange?.("ticketmasterConsumerKey", value)}
              disabled={disabled}
            />
            {cloneProtectedFieldModes.ticketmasterConsumerKey === "replace" ? (
              <MaskedCredentialInput
                {...txt("ticketmasterConsumerKey")}
                placeholder="Enter a new Ticketmaster consumer key"
              />
            ) : null}
            {cloneProtectedFieldModes.ticketmasterConsumerKey === "clone" ? (
              <p className="text-xs text-muted-foreground">
                The backend will securely copy the source consumer key without displaying it.
              </p>
            ) : null}
            {cloneProtectedFieldModes.ticketmasterConsumerKey === "global" ? (
              <p className="text-xs text-muted-foreground">
                The destination device will inherit the global Ticketmaster consumer key.
              </p>
            ) : null}
            {fieldError("ticketmasterConsumerKey")}
          </>
        ) : (
          <MaskedCredentialInput
            {...txt("ticketmasterConsumerKey")}
            placeholder={mode === "edit" ? "Leave blank to keep current" : "Leave blank to use global default"}
          />
        )}
      </FieldRow>

      {/* ── 16. Security (create only) ── */}
      {mode === "add" && (
        <>
          <SectionHeader title={ALFRED_DEVICE_SECTION_TITLES.security} />
          <FieldRow label={getAlfredDeviceFieldLabel("configurationPassword")}>
            {isClone ? (
              <>
                <CloneProtectedFieldChoice
                  field="configurationPassword"
                  value={cloneProtectedFieldModes.configurationPassword}
                  onChange={(value) => onCloneProtectedFieldModeChange?.("configurationPassword", value)}
                  disabled={disabled}
                />
                {cloneProtectedFieldModes.configurationPassword === "replace" ? (
                  <Input
                    type="password"
                    value={data.configurationPassword}
                    onChange={(e) => onChange("configurationPassword", e.target.value)}
                    onBlur={() => onBlur?.("configurationPassword")}
                    disabled={disabled}
                    autoComplete="new-password"
                    placeholder="Enter a new configuration password"
                    aria-invalid={errors.configurationPassword && touched.configurationPassword ? true : undefined}
                    className="font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
                  />
                ) : null}
                {cloneProtectedFieldModes.configurationPassword === "clone" ? (
                  <p className="text-xs text-muted-foreground">
                    The backend will securely copy the source configuration password without displaying it.
                  </p>
                ) : null}
                {cloneProtectedFieldModes.configurationPassword === "global" ? (
                  <p className="text-xs text-muted-foreground">
                    The destination device will inherit the global configuration password.
                  </p>
                ) : null}
                {fieldError("configurationPassword")}
              </>
            ) : (
              <Input
                type="password"
                value={data.configurationPassword}
                onChange={(e) => onChange("configurationPassword", e.target.value)}
                disabled={disabled}
                autoComplete="new-password"
                placeholder="Leave blank to use default password"
                className="font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
              />
            )}
          </FieldRow>
        </>
      )}

    </div>
  )
}
